import { FC, useMemo, useState } from 'react'

import { Button, ButtonSizes, ButtonVariants } from '@inno-ui/button'
import { CodeIcon, EyeLineIcon } from '@inno-ui/icons'
import { toast } from '@inno-ui/toast'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  GET_ARTIFACTS_QUERY_KEY,
  REVIEW_QUEUE_QUERY_KEY,
  useReviewArtifactMutation
} from 'shared/api/forgeApi'

import { AiReviewResult } from '../../types'
import { RevokeCardProps } from './RevokeCard.types'
import { CardShell } from './components/CardShell'
import { CodeModal } from './components/CodeModal'
import { DecisionForm } from './components/DecisionForm'
import { RevealModal } from './components/RevealModal'
import { reviewFeedback } from './components/reviewFeedback'

// Approved widget reviewer surface — single destructive action (revoke).
// No host editor, no AI review button: once approved, the reviewer is only
// here to take the widget down. Reveal + code remain so they can audit it.
export const RevokeCard: FC<RevokeCardProps> = ({ artifact }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })
  const queryClient = useQueryClient()
  const publicId = artifact.public_id
  const [codeOpen, setCodeOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  // verdict is read-only here — show the chip from when the widget was
  // reviewed, but never trigger a fresh AI pass on a live widget.
  const ai = useMemo<AiReviewResult | null>(
    () =>
      artifact.ai_review_verdict
        ? {
            verdict: artifact.ai_review_verdict,
            findings: artifact.ai_review_findings ?? []
          }
        : null,
    [artifact.ai_review_verdict, artifact.ai_review_findings]
  )

  const reviewMutation = useReviewArtifactMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REVIEW_QUEUE_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [GET_ARTIFACTS_QUERY_KEY] })
      toast.success(reviewFeedback('revoke', 'success', t))
    },
    onError: () => {
      toast.error(reviewFeedback('revoke', 'failed', t))
    }
  })

  const submit = (_decision: 'approve' | 'decline', reason?: string) =>
    reviewMutation.mutate({
      publicId,
      decision: 'decline',
      reason
    })

  return (
    <CardShell
      artifact={artifact}
      verdict={ai}
      actions={
        <>
          <Button
            variant={ButtonVariants.Light}
            size={ButtonSizes.S}
            icon={EyeLineIcon}
            label={t('preview')}
            onClick={() => setPreviewOpen(true)}
          />
          <Button
            variant={ButtonVariants.Light}
            size={ButtonSizes.S}
            icon={CodeIcon}
            label={t('view_code')}
            onClick={() => setCodeOpen(true)}
          />
        </>
      }
    >
      <DecisionForm
        variant='revoke'
        busy={reviewMutation.isPending}
        onSubmit={submit}
      />
      <RevealModal
        publicId={publicId}
        title={artifact.title}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
      <CodeModal
        publicId={publicId}
        title={artifact.title}
        isOpen={codeOpen}
        onClose={() => setCodeOpen(false)}
      />
    </CardShell>
  )
}
